import type { FC } from "react";

import { motion, useReducedMotion } from "framer-motion";

import type {
  PlanSnapshotGroupedColumn,
  PlanSnapshotNodeWithDependencyCounts,
} from "../../types";

import {
  AccentCard,
  CARD_DESC_CLASS,
  CARD_META_CLASS,
  CARD_STATUS_CLASS,
  CARD_TITLE_CLASS,
  EmptyState,
  StatusDot,
  getStatusAccent,
  getStatusDot,
  isActiveStatus,
} from "../common";

function riskClassName(risk: string): string {
  switch (risk.toLowerCase()) {
    case "high": {
      return "border-l-rose-500 text-rose-500 dark:text-rose-400";
    }
    case "medium": {
      return "border-l-amber-500 text-amber-500 dark:text-amber-400";
    }
    default: {
      return "border-l-emerald-500 text-emerald-500 dark:text-emerald-400";
    }
  }
}

interface PlanSnapshotKanbanColumnProps {
  column: PlanSnapshotGroupedColumn;
  query: string;
  onSelectNode: (node: PlanSnapshotNodeWithDependencyCounts) => void;
}

export const PlanSnapshotKanbanColumn: FC<PlanSnapshotKanbanColumnProps> = ({
  column,
  query,
  onSelectNode,
}) => {
  const prefersReducedMotion = useReducedMotion();
  const hasSearch = Boolean(query);
  const emptyCopy = hasSearch
    ? "No matches in this column."
    : "No tasks in this column.";

  return (
    <div className="flex w-[85vw] flex-shrink-0 snap-start flex-col sm:w-[320px]">
      {/* Column header */}
      <div className="mb-2 flex items-center justify-between border-b border-slate-200 pb-2 dark:border-slate-700">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 bg-slate-400 dark:bg-slate-500" />
          <span className="font-mono text-[11px] font-bold uppercase tracking-widest text-slate-700 dark:text-slate-300">
            {column.title}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-mono text-[10px] text-slate-400 dark:text-slate-500">
            {column.dependency_count}d · {column.dependent_count}dep
          </span>
          <span className="border border-slate-300 bg-slate-100 px-1.5 py-px font-mono text-[10px] font-bold text-slate-600 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-400">
            {column.nodes.length}
          </span>
        </div>
      </div>

      {/* Column body */}
      <div className="custom-scrollbar flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto">
        {column.nodes.length === 0 ? (
          <EmptyState size="sm" title={emptyCopy} />
        ) : (
          column.nodes.map((node, index) => (
            <motion.div
              key={node.node_id}
              initial={prefersReducedMotion ? false : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={
                prefersReducedMotion
                  ? { duration: 0 }
                  : {
                      delay: Math.min(index * 0.03, 0.2),
                      duration: 0.25,
                      ease: "easeOut",
                    }
              }
            >
              <AccentCard
                accent={getStatusAccent(node.status)}
                onClick={() => onSelectNode(node)}
              >
                {/* Title row */}
                <div className="flex items-center gap-2">
                  <StatusDot
                    color={getStatusDot(node.status)}
                    pulse={isActiveStatus(node.status)}
                  />
                  <span className={CARD_TITLE_CLASS}>{node.issue_key}</span>
                  {node.is_story && (
                    <span className="border border-emerald-500/30 bg-emerald-500/10 px-1 py-px font-mono text-[9px] font-bold uppercase tracking-widest text-emerald-600 dark:text-emerald-300">
                      Story
                    </span>
                  )}
                  {node.status && (
                    <span className={`${CARD_STATUS_CLASS} ml-auto`}>
                      {node.status}
                    </span>
                  )}
                </div>

                {/* Description */}
                <p
                  className={CARD_DESC_CLASS}
                  title={
                    node.title ??
                    node.humanized_description ??
                    node.description ??
                    undefined
                  }
                >
                  {node.title ??
                    node.humanized_description ??
                    node.description ??
                    "Untitled task"}
                </p>

                {/* Metadata row */}
                {(node.depends_on_count > 0 ||
                  node.unresolved_dependency_count > 0 ||
                  node.self_assessed_risk) && (
                  <div
                    className={`mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-slate-200 pt-1.5 dark:border-slate-700 ${CARD_META_CLASS}`}
                  >
                    {node.depends_on_count > 0 && (
                      <span>
                        {node.depends_on_count} dep
                        {node.depends_on_count !== 1 && "s"}
                      </span>
                    )}
                    {node.unresolved_dependency_count > 0 && (
                      <span className="text-amber-500 dark:text-amber-400">
                        {node.unresolved_dependency_count} unresolved
                      </span>
                    )}
                    {node.self_assessed_risk && (
                      <span
                        className={`border-l-2 pl-1.5 font-bold uppercase ${riskClassName(node.self_assessed_risk)}`}
                      >
                        {node.self_assessed_risk}
                      </span>
                    )}
                  </div>
                )}
              </AccentCard>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
};
